import { useRef, useState } from 'react';
import { LuClock } from 'react-icons/lu';
import { usePriorStudies } from '../hooks/usePriorStudies';
import { useClickOutside } from '../hooks/useClickOutside';
import PriorStudyRow from './PriorStudyRow';
import styles from './PriorStudies.module.css';

interface PriorStudiesProps {
  patientUuid: string;
  currentStudyUuid: string;
}

export default function PriorStudies({
  patientUuid,
  currentStudyUuid,
}: PriorStudiesProps) {
  const { priors } = usePriorStudies({ patientUuid, currentStudyUuid });
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useClickOutside(rootRef, () => setOpen(false), open);

  if (priors.length === 0) return null;

  if (priors.length === 1) {
    return (
      <div className={styles.root}>
        <PriorStudyRow study={priors[0]} variant="inline" />
      </div>
    );
  }

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={styles.badge}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${priors.length} Prior Studies`}
      >
        <LuClock className={styles.clockIcon} aria-hidden />
        <span className={styles.count}>{priors.length}</span>
        <span className={styles.badgeLabel}>Prior Studies</span>
        <svg
          className={styles.chevron}
          data-open={open}
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <polyline points="4 6 8 10 12 6" />
        </svg>
      </button>
      {open && (
        <div className={styles.dropdown} role="menu">
          <div className={styles.dropdownHeader}>
            <span className={styles.dropdownTitle}>Prior Studies</span>
            <span className={styles.dropdownCount}>
              {priors.length} available
            </span>
          </div>
          {priors.map((study) => (
            <PriorStudyRow key={study.uuid} study={study} variant="dropdown" />
          ))}
        </div>
      )}
    </div>
  );
}
